import React, { useState } from 'react';
import {
  Box,
  Dialog,
  DialogContent,
  DialogTitle,
  Divider,
  SvgIcon,
  SxProps,
  Tab,
  Tabs,
} from '@mui/material';
import { Settings as SettingsIcon } from '@mui/icons-material';

import { getCoursesState } from '../../../../../../store/V2/CoursesSlice';
import { useAppSelector } from '../../../../../../hooks';
import BasicDetailsTab from './BasicDetailsTab';
import TutorsTab from './TutorsTab';
import StudentsTab from './StudentsTab';
import PricingTab from './PricingTab';
import OthersTab from './Others';
// SVG
import { ReactComponent as BasicDetailsIcon } from './icons/basicDetails.svg';
import { ReactComponent as PricingDetailsIcon } from './icons/pricing.svg';
import { ReactComponent as TutorsIcon } from './icons/tutors.svg';
import { ReactComponent as StudentsIcon } from './icons/students.svg';
import {
  getTenantsState,
  TUTOPIYA_TENANT_KEY,
} from '../../../../../../store/V2/TenantsSlice';

interface CourseOptionsModalProps {
  open: boolean;
  onClose: () => void;
}

const a11yProps = (index: number) => {
  return {
    id: `tab-item-${index}`,
    'aria-controls': `tabpanel-${index}`,
  };
};

const tabIconStyles: SxProps = {
  mr: '10px',
  width: '25px',
  height: '25px',
};

const tabItemStyles: SxProps = {
  p: '20px',
  color: '#1E293B',
  fontWeight: 500,
  fontSize: '16px',
  transition: 'all ease .3s',
  '&.Mui-selected': {
    color: '#1E293B', // Change this to your desired color
  },
  '&:hover': {
    cursor: 'pointer',
    backgroundColor: '#e2e8f0', // Change this to your desired hover background color
  },
};

const tabs = [
  {
    index: 0,
    key: 'basicDetails',
    title: 'Basic Details',
    icon: (
      <SvgIcon sx={tabIconStyles}>
        <BasicDetailsIcon />
      </SvgIcon>
    ),
  },
  {
    index: 1,
    key: 'pricing',
    title: 'Pricing',
    icon: (
      <SvgIcon sx={tabIconStyles}>
        <PricingDetailsIcon />
      </SvgIcon>
    ),
  },
  {
    index: 2,
    key: 'tutors',
    title: 'Tutors',
    icon: (
      <SvgIcon sx={tabIconStyles}>
        <TutorsIcon />
      </SvgIcon>
    ),
  },
  {
    index: 3,
    key: 'students',
    title: 'Students',
    icon: (
      <SvgIcon sx={tabIconStyles}>
        <StudentsIcon />
      </SvgIcon>
    ),
  },
  {
    index: 4,
    key: 'options',
    title: 'Options',
    icon: <SettingsIcon sx={tabIconStyles} />,
  },
];

const CourseOptionsModal: React.FC<CourseOptionsModalProps> = ({
  open,
  onClose,
}) => {
  // State
  const [selectedTabValue, setSelectedTab] = useState(0);

  // Store
  const coursesState = useAppSelector(getCoursesState);
  const tenantsState = useAppSelector(getTenantsState);

  const { appTenant } = tenantsState;
  const { adminCourse: courseData } = coursesState;

  const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
    setSelectedTab(newValue);
  };

  const renderTabPanes = () => {
    let panel: React.ReactElement | null = null;

    switch (selectedTabValue) {
      case 0:
        panel = <BasicDetailsTab onClose={onClose} />;
        break;

      case 1:
        panel = <PricingTab onClose={onClose} />;
        break;

      case 2:
        panel = <TutorsTab onClose={onClose} />;
        break;

      case 3:
        panel = <StudentsTab onClose={onClose} />;
        break;

      case 4:
        panel = <OthersTab onClose={onClose} />;
        break;

      default:
        panel = null;
        break;
    }

    return panel;
  };

  return (
    <Dialog
      fullWidth
      maxWidth="lg"
      open={open}
      onClose={onClose}
      PaperProps={{
        sx: { p: '0', borderRadius: '20px' },
      }}
    >
      <DialogTitle
        sx={{
          fontWeight: 600,
          fontSize: '16px',
          color: '#000000',
          p: '30px',
        }}
      >
        Course Details
      </DialogTitle>
      <DialogContent style={{ padding: '0' }}>
        <div style={{ padding: '0 40px' }}>
          <Tabs
            value={selectedTabValue}
            onChange={handleTabChange}
            TabIndicatorProps={{
              sx: { backgroundColor: '#01A79F', height: '4px' },
            }}
          >
            {tabs.map((item, index) => {
              let isDisabled = false;

              if (item.key === 'students') {
                if (
                  courseData &&
                  appTenant &&
                  appTenant.name === TUTOPIYA_TENANT_KEY &&
                  !courseData.isOneToOne &&
                  !courseData.groupClassId
                ) {
                  isDisabled = true;
                }
              }

              if (item.key === 'pricing') {
                isDisabled = Boolean(courseData?.isOneToOne);
              }

              return (
                <Tab
                  disabled={isDisabled}
                  key={`tab-item-${index}`}
                  {...a11yProps(0)}
                  icon={item.icon}
                  iconPosition="start"
                  label={item.title}
                  sx={{ ...tabItemStyles, opacity: isDisabled ? 0.6 : 1 }}
                />
              );
            })}
          </Tabs>
        </div>
        <Divider />
        <Box>{renderTabPanes()}</Box>
      </DialogContent>
    </Dialog>
  );
};

export default CourseOptionsModal;
